import React, { createContext, useContext, useRef, useEffect, useMemo, useState } from 'react';
import type { AnomalyBandWithForm, AnomalyBandsConfig } from './types';

const AnomalyBandContext = createContext<AnomalyBandsConfig | null>(null);

export function useAnomalyBandContext(): AnomalyBandsConfig | null {
  return useContext(AnomalyBandContext);
}

interface AnomalyBandProviderProps {
  endpointId: string;
  children: React.ReactNode;
  initialBands?: AnomalyBandWithForm[];
  initialSelectedBandId?: string | null;
  keepSingleBandSelected?: boolean;
  readOnly?: boolean;
}

export function AnomalyBandProvider({
  endpointId,
  children,
  initialBands = [],
  initialSelectedBandId = null,
  keepSingleBandSelected = false,
  readOnly = false,
}: AnomalyBandProviderProps): React.ReactElement {
  const [bands, setBands] = useState<AnomalyBandWithForm[]>(initialBands);
  const [selectedBandId, setSelectedBandId] = useState<string | null>(initialSelectedBandId ?? null);
  const [pendingDeleteBandId, setPendingDeleteBandId] = useState<string | null>(null);
  const updatersRef = useRef<Set<(bands: AnomalyBandWithForm[], selectedBandId: string | null) => void>>(new Set());
  const loadedForEndpointRef = useRef<string | null>(null);
  const persistedSignatureRef = useRef<string | null>(null);

  // Identity of the persisted (server) bands. Changes when an anomaly/anchor is
  // added or deleted elsewhere (e.g. the Insights tab), but not on a plain refetch
  // that returns the same set — so in-progress field edits are not clobbered.
  const persistedSignature = useMemo(
    () => initialBands.filter((b) => b.persistedId).map((b) => b.persistedId).join('|'),
    [initialBands]
  );

  // Sync initialBands on first load / endpoint change, and re-sync when the
  // server band set changes (add/delete elsewhere) while keeping unsaved local bands.
  useEffect(() => {
    if (loadedForEndpointRef.current !== endpointId) {
      loadedForEndpointRef.current = endpointId;
      persistedSignatureRef.current = persistedSignature;
      setBands(initialBands);
      setSelectedBandId(initialSelectedBandId ?? null);
      return;
    }

    if (persistedSignatureRef.current !== persistedSignature) {
      persistedSignatureRef.current = persistedSignature;
      const unsaved = bands.filter((b) => !b.persistedId);
      const next = [...initialBands, ...unsaved];
      setBands(next);
      if (selectedBandId && !next.some((b) => b.id === selectedBandId)) {
        setSelectedBandId(null);
      }
    }
  }, [endpointId, initialBands, initialSelectedBandId, persistedSignature, bands, selectedBandId]);

  const registerUpdater = (updater: (bands: AnomalyBandWithForm[], selectedBandId: string | null) => void) => {
    updatersRef.current.add(updater);
    return () => {
      updatersRef.current.delete(updater);
    };
  };

  useEffect(() => {
    updatersRef.current.forEach((fn) => fn(bands, selectedBandId));
  }, [bands, selectedBandId]);

  useEffect(() => {
    if (pendingDeleteBandId && pendingDeleteBandId !== selectedBandId) {
      setPendingDeleteBandId(null);
    }
  }, [pendingDeleteBandId, selectedBandId]);

  const value: AnomalyBandsConfig = {
    bands,
    setBands,
    selectedBandId,
    setSelectedBandId,
    keepSingleBandSelected,
    readOnly,
    pendingDeleteBandId,
    setPendingDeleteBandId,
    registerUpdater,
  };

  return (
    <AnomalyBandContext.Provider value={value}>
      {children}
    </AnomalyBandContext.Provider>
  );
}
